import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// ─── Prompt Data ──────────────────────────────────────────────────────────────
const PROMPTS = [
  {
    id: 'job-roadmap',
    tag: 'Career',
    category: 'Career',
    title: 'Job Description Analyzer',
    subtitle: 'Full job-cracking roadmap · for students, freshers & early-career professionals',
    description:
      'Paste any job description and get a complete 13-section roadmap: skill gap analysis, ATS keywords, resume bullet templates, 7-day prep plan, mock interview questions, referral messages, and a final verdict on whether to apply now.',
    includes: [
      'JD summary + fresher suitability score',
      'Must-have & nice-to-have skills table',
      'ATS keywords mapped to resume sections',
      'Resume improvement plan + 5 bullet templates',
      '3 beginner project ideas with resume bullets',
      'Skill gap analysis with free resource types',
      '7-day preparation plan (2–4 hrs/day)',
      'Predicted interview rounds + prep tips',
      '10 technical + 10 project + 10 HR questions',
      'Referral message, recruiter DM & email format',
      'Final action checklist + Apply Now verdict',
    ],
    steps: [
      { num: '01', label: 'Copy the prompt', desc: 'Hit "Copy Prompt" to copy the full text.' },
      { num: '02', label: 'Paste into AI', desc: 'Open ChatGPT, Claude, or Gemini and paste it.' },
      { num: '03', label: 'Add the JD', desc: 'Replace [Paste the full job description here] with the real JD.' },
      { num: '04', label: 'Get your roadmap', desc: 'Read the 13-section plan, start with the action checklist.' },
    ],
    text: `Act as an expert career coach, recruiter, ATS resume reviewer, resume writer, and interview preparation mentor.

I will paste a job description below. Your task is to analyze it deeply and convert it into a complete job-cracking roadmap for students, freshers, and early-career professionals.

Important rules:

1. Use only the job description I provide.
2. Do not give generic advice.
3. Do not suggest fake experience.
4. Do not invent skills, projects, internships, certifications, or achievements.
5. If a skill or requirement is missing, clearly say it is a gap.
6. Make the advice practical for beginners, students, freshers, and early-career professionals.
7. Keep the output structured, simple, and actionable.
8. Explain everything in beginner-friendly language.
9. If the job description is vague, mention what can and cannot be understood from it.
10. Focus on what I should do before applying to improve my chances.

Job description:

[Paste the full job description here]

Now give me the output in this structure:

---

## 1. JOB SUMMARY

Explain:

- What this job is actually about
- Main responsibilities of the role
- What type of candidate the company is looking for
- Whether this role is beginner-friendly, intermediate, or advanced
- Whether a fresher/student should apply or not
- Give a realistic reason for your answer

Also give:

JD Difficulty Level: Beginner / Intermediate / Advanced
Fresher Suitability Score: 0–100

---

## 2. MUST-HAVE SKILLS FROM THE JD

Create a table with:

| Skill | Why it is needed | Priority: High/Medium/Low | How to prove this skill in a resume |

Only include skills that are clearly mentioned or strongly required in the job description.

---

## 3. NICE-TO-HAVE SKILLS

Create a table with:

| Skill | Is it mandatory? | Time to learn basics | Best way to show it |

Keep the learning estimate realistic for a beginner.

---

## 4. ATS KEYWORDS TO ADD IN RESUME

Extract keywords directly from the job description and divide them into:

- Technical keywords
- Role-specific keywords
- Tools/platforms
- Soft skills
- Action verbs

Then tell me where to add these keywords:

| Keyword | Resume section | How to use it naturally |

Resume sections can include:

- Summary
- Skills
- Projects
- Internship/Experience
- Education
- Certifications

Do not suggest adding a keyword if the candidate cannot honestly support it.

---

## 5. RESUME IMPROVEMENT PLAN

Based only on the job description, tell me:

- What a candidate should add to their resume
- What they should remove or reduce
- What they should highlight more
- What type of projects they should include
- What gaps a recruiter may notice
- How to make the resume more ATS-friendly for this job

Then give 5 strong resume bullet point templates using this format:

Action verb + skill/tool + task + result/impact

Rules:

- Do not invent fake numbers.
- Use honest beginner-friendly wording.
- Make each bullet specific to this job.
- Keep each bullet resume-ready.

---

## 6. PROJECT ADD-ONS

Suggest 3 project ideas that can improve a candidate's chances for this job.

For each project, give:

- Project title
- Why this project matches the job description
- Features to build
- Tech stack/tools
- Skills demonstrated
- Difficulty level
- Estimated time to complete basic version
- Resume bullet for the project
- How to explain it in an interview

Projects should be realistic for students, freshers, and beginners.

---

## 7. SKILL GAP ANALYSIS

Create a table:

| Requirement from JD | Is it beginner-friendly? Yes/No/Partial | What to learn | Time needed | Best free resource type |

For "Best free resource type," suggest only categories such as:

- YouTube tutorial
- Official documentation
- Free practice platform
- GitHub examples
- Blog/article
- Free course

---

## 8. 7-DAY PREPARATION PLAN

Create a strict 7-day preparation plan for this job.

Each day should include:

- What to learn
- What to revise
- What to build or practice
- Resume improvement task
- Interview questions to prepare

Make the plan realistic for someone who can spend 2–4 hours per day.

---

## 9. POSSIBLE INTERVIEW PROCESS

Predict the possible interview rounds based on the job description:

- HR screening
- Technical round
- Assignment/task
- Managerial round
- Final discussion

For each round, tell me:

- What they may ask
- How to prepare
- Mistakes to avoid

---

## 10. TOP INTERVIEW QUESTIONS

Give me:

- 10 technical questions
- 10 project-based questions
- 10 HR questions
- 5 questions to ask the interviewer

For each question, also give a short answer strategy.

---

## 11. HOW TO APPLY PROPERLY

Tell me:

- Should a candidate apply directly or try for a referral?
- What should they write in the application note?
- What should they message a recruiter?
- What should they message an employee for referral?
- What mistakes should they avoid while applying?

Give me:

- 1 short referral message
- 1 recruiter message
- 1 cold DM message
- 1 email format

Keep all messages short, polite, and professional.

---

## 12. FINAL ACTION CHECKLIST

Create a checklist to complete before applying:

| Task | Priority | Time needed | Done? |

Include:

- Resume changes
- Project changes
- Skills to revise
- Interview preparation
- Application steps

---

## 13. FINAL VERDICT

End with one clear verdict:

- Apply now
- Apply after improving resume
- Not suitable yet

Also include:

- Best next step
- Biggest risk in the application
- Fastest improvement before applying
- One honest sentence about chances`,
  },
  {
    id: 'fat-loss',
    tag: 'Fat Loss',
    category: 'Fitness',
    title: 'Complete Fat-Loss Plan',
    subtitle: 'South Indian (Telangana / AP) food · personalised to your details',
    description:
      'Generates a full personalised fat-loss plan: BMI calculation, daily meal plan using Telangana/AP foods, weekly workout schedule, supplement guide, and monthly budget breakdown.',
    includes: [
      'BMI + calorie deficit calculation',
      'Full daily meal plan (Idli, Pesarattu, Dal, Curd rice…)',
      'Weekly workout + walking schedule',
      'Daily rules: food, sleep, cheat meals',
      '"Do Not Do This" guardrails',
      'Monthly cost — low / medium / high budget',
      'Motivational coach ending',
    ],
    text: `Act as my strict but practical fat-loss coach.

I want a complete fat-loss plan based on my personal details. My food should be mainly South Indian, especially Telangana and Andhra Pradesh style food. The plan should be simple enough for a student or working person to follow.

My details:

Height: [ENTER YOUR HEIGHT]
Weight: [ENTER YOUR CURRENT WEIGHT]
Age: [ENTER YOUR AGE]
Gender: [ENTER YOUR GENDER]
Current role: [Student / Job / Startup / Work from home / Other]

Important instructions:

1. First calculate my basic body status:
   - BMI
   - Approximate maintenance calories
   - Fat-loss calorie target
   - Daily calorie deficit
   - Expected weekly fat-loss range
   - Realistic timeline for visible results

2. Explain calorie deficit in simple words:
   - What is calorie deficit?
   - Why is it needed for fat loss?
   - Why extreme dieting is dangerous?
   - How I should follow it without losing energy, focus, or muscle?

3. Give me a strict daily schedule based on my current role.
   If I am a student, make the schedule around classes and study time.
   If I am doing a job, make the schedule around office hours.
   If I am working in a startup, make it realistic for a busy and unpredictable day.
   If I work from home, include movement breaks and home workout timing.

4. Give me a full-day meal plan using Telangana and Andhra Pradesh foods.
   Use normal home foods like:
   - Idli
   - Dosa
   - Upma
   - Pesarattu
   - Rice
   - Dal
   - Sambar
   - Rasam
   - Curd rice
   - Egg
   - Chicken
   - Fish
   - Paneer
   - Curd
   - Buttermilk
   - Vegetables
   - Pickle in controlled quantity

5. Do not give too many choices.
   Give one strict plan for:
   - Breakfast
   - Mid-morning
   - Lunch
   - Evening snack
   - Dinner
   - Water intake
   - Sleep timing

6. Give exact portion guidance in simple Indian measurements:
   - Cups
   - Spoons
   - Pieces
   - Bowls
   - Number of eggs
   - Grams only where needed

7. Give a strict weekly workout plan:
   - Strength training days
   - Walking/cardio days
   - Rest day
   - Daily step target
   - Beginner-friendly exercises
   - No extreme workout plan

8. Give supplement guidance strictly.
   Do not suggest fat burners, detox drinks, slimming tablets, or unsafe products.
   Tell me clearly:
   - Supplement name
   - Why it is needed
   - When to take it
   - How much to take
   - Who should avoid it
   If no supplement is required, say clearly: "No supplement required right now."

9. Give me rules to follow every day:
   - Food rules
   - Workout rules
   - Sleep rules
   - Cheat meal rules
   - Tracking rules
   - Weekly weight-check rule

10. Give me a "Do Not Do This" section:
   - No crash dieting
   - No skipping meals
   - No fat burners
   - No sugar drinks
   - No overeating rice at night
   - No comparing with others
   - No quitting after one bad day

11. Give me estimated monthly cost in India in 3 levels:
   - Low budget plan
   - Medium budget plan
   - High budget plan

   For each cost level, include:
   - Food cost
   - Workout cost
   - Supplement cost
   - Total estimated monthly cost

12. Make the final plan very clear and strict.
   Do not say "maybe," "you can try," or "optional."
   Give me one proper plan and tell me exactly how to follow it.

13. End with emotional motivation.
   Talk to me like a real coach.
   Remind me that fat loss is not magic, it is daily discipline.
   Tell me that even if I failed before, I can restart today.
   Make me feel confident that I can reduce fat, become healthier, and transform my life.`,
  },
];

const STEPS = [
  { num: '01', label: 'Fill details', desc: 'Replace every [ENTER YOUR …] with your real values.' },
  { num: '02', label: 'Copy prompt', desc: 'Hit "Copy Prompt" — the full text is in your clipboard.' },
  { num: '03', label: 'Paste into any AI', desc: 'Open ChatGPT, Claude, or Gemini and paste it in.' },
  { num: '04', label: 'Start today', desc: "Read your plan. Pick one action. Begin now — not Monday." },
];

// ─── Category colour helper ───────────────────────────────────────────────────
const CATEGORY_COLORS = {
  Fitness: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  Career: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  default: { bg: 'bg-accent/10', text: 'text-accent', dot: 'bg-accent' },
};
function catColor(cat) {
  return CATEGORY_COLORS[cat] ?? CATEGORY_COLORS.default;
}

// ─── Sidebar item ─────────────────────────────────────────────────────────────
function SidebarItem({ prompt, active, onClick }) {
  const c = catColor(prompt.category);
  return (
    <button
      onClick={onClick}
      className={`w-full rounded-xl px-4 py-3.5 text-left transition-all duration-200 ${
        active
          ? 'bg-ink text-white shadow-ambient'
          : 'text-ink hover:bg-ink/5'
      }`}
    >
      <div className="flex items-center gap-2.5">
        <span className={`h-2 w-2 shrink-0 rounded-full ${active ? 'bg-white/60' : c.dot}`} />
        <span className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${active ? 'text-white/60' : c.text}`}>
          {prompt.category}
        </span>
      </div>
      <p className={`mt-1.5 text-sm font-semibold leading-snug ${active ? 'text-white' : 'text-ink'}`}>
        {prompt.title}
      </p>
    </button>
  );
}

// ─── Main prompt view ─────────────────────────────────────────────────────────
function PromptView({ prompt }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(prompt.text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = prompt.text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <motion.div
      key={prompt.id}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col gap-8"
    >
      {/* Title block */}
      <div>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] ${catColor(prompt.category).bg} ${catColor(prompt.category).text}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${catColor(prompt.category).dot}`} />
          {prompt.category}
        </span>
        <h1 className="mt-3 font-display text-3xl font-bold uppercase leading-tight tracking-[-0.025em] text-ink md:text-4xl">
          {prompt.title}
        </h1>
        <p className="mt-2 text-sm text-ink/55">{prompt.subtitle}</p>
        <p className="copy-refined mt-4 max-w-2xl text-sm leading-relaxed text-ink/70">
          {prompt.description}
        </p>
      </div>

      {/* What's included */}
      <div className="rounded-2xl border border-ink/10 bg-snow/70 p-5 md:p-6">
        <p className="mb-4 text-[10px] font-semibold uppercase tracking-[0.24em] text-ink/45">
          What this prompt generates
        </p>
        <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {prompt.includes.map((item) => (
            <li key={item} className="flex items-start gap-2.5 text-sm text-ink/72">
              <svg className="mt-0.5 shrink-0 text-ink/35" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* How to use steps */}
      <div>
        <p className="mb-4 text-[10px] font-semibold uppercase tracking-[0.24em] text-ink/45">
          How to use
        </p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {(prompt.steps ?? STEPS).map((s) => (
            <div key={s.num} className="rounded-xl border border-ink/10 bg-snow/60 p-4">
              <span className="font-display text-2xl font-bold text-ink/12">{s.num}</span>
              <p className="mt-1 text-[12px] font-semibold text-ink">{s.label}</p>
              <p className="mt-1 text-[11px] leading-snug text-ink/55">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Prompt box */}
      <div className="overflow-hidden rounded-2xl border border-ink/12 bg-[#f7f6f4]">
        {/* Toolbar */}
        <div className="flex items-center justify-between border-b border-ink/10 bg-ink/4 px-4 py-3 md:px-5">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
            <span className="ml-2 font-mono text-[10px] text-ink/35 sm:ml-3 sm:text-[11px]">{prompt.id}-prompt.txt</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden text-[10px] font-semibold uppercase tracking-[0.18em] text-ink/35 lg:block">
              Paste into ChatGPT / Claude / Gemini
            </span>
            <motion.button
              onClick={handleCopy}
              whileTap={{ scale: 0.96 }}
              className="flex items-center gap-1.5 rounded-full border border-ink/20 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink transition-all hover:border-ink/40 hover:bg-ink hover:text-white md:px-4"
            >
              <AnimatePresence mode="wait" initial={false}>
                {copied ? (
                  <motion.span
                    key="ok"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.18 }}
                    className="flex items-center gap-1.5"
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Copied
                  </motion.span>
                ) : (
                  <motion.span
                    key="cp"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.18 }}
                    className="flex items-center gap-1.5"
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    Copy Prompt
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>

        {/* Prompt text — scrollable */}
        <div className="max-h-[420px] overflow-y-auto p-4 md:max-h-[520px] md:p-6">
          <pre className="whitespace-pre-wrap font-mono text-[11.5px] leading-[1.85] text-ink/75 md:text-[12.5px]">
            {prompt.text}
          </pre>
        </div>

        {/* Copy success */}
        <AnimatePresence>
          {copied && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden border-t border-green-500/20 bg-green-50 px-5 py-3 md:px-6"
            >
              <p className="text-sm font-medium text-green-700">
                Copied — paste into your AI, fill the <span className="rounded bg-green-100 px-1 font-mono text-xs">[ENTER YOUR …]</span> fields, and send.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
function ContentPage() {
  const [activeId, setActiveId] = useState(PROMPTS[0].id);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const active = PROMPTS.find((p) => p.id === activeId) ?? PROMPTS[0];

  return (
    <div className="min-h-screen bg-paper text-ink">
      {/* Top bar */}
      <header className="sticky top-0 z-50 border-b border-ink/10 bg-paper/90 backdrop-blur-sm">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-3.5 md:px-8">
          <div className="flex items-center gap-4">
            {/* Mobile sidebar toggle */}
            <button
              className="flex items-center gap-1.5 rounded-lg border border-ink/15 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-ink/60 transition hover:bg-ink/5 md:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle content list"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
              Content
            </button>

            <Link
              to="/"
              className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/45 transition-colors hover:text-ink"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Portfolio
            </Link>
          </div>

          <span className="hidden font-display text-sm font-bold uppercase tracking-[-0.01em] text-ink sm:block">
            Nithin's Content
          </span>

          <a
            href="https://www.instagram.com/nithinnsai?igsh=MXJ3bHIxZXdzc3h2cg%3D%3D&utm_source=qr"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white transition-opacity hover:opacity-80"
            style={{
              background:
                'linear-gradient(135deg,#f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)'
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
            </svg>
            Follow
          </a>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1400px]">
        {/* ── Sidebar ─────────────────────────────────────────────────────── */}
        {/* Mobile overlay */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-30 bg-ink/30 md:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Sidebar panel */}
        <aside
          className={`
            fixed left-0 top-[53px] z-40 h-[calc(100vh-53px)] w-72 shrink-0 overflow-y-auto border-r border-ink/10 bg-snow/80 backdrop-blur-sm transition-transform duration-300
            md:sticky md:translate-x-0
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          `}
        >
          <div className="p-4">
            <p className="mb-3 px-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-ink/35">
              AI Prompts
            </p>
            <nav className="space-y-1" aria-label="Content list">
              {PROMPTS.map((p) => (
                <SidebarItem
                  key={p.id}
                  prompt={p}
                  active={p.id === activeId}
                  onClick={() => {
                    setActiveId(p.id);
                    setSidebarOpen(false);
                  }}
                />
              ))}
            </nav>

            {/* Coming soon */}
            <div className="mt-6 rounded-xl border border-dashed border-ink/15 p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/35">
                Coming soon
              </p>
              {['Startup Idea Validator', 'Study Plan Builder', 'Resume Rewriter'].map((t) => (
                <div key={t} className="mt-2.5 flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-ink/15" />
                  <span className="text-xs text-ink/35">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* ── Main content ─────────────────────────────────────────────────── */}
        <main className="min-w-0 flex-1 px-4 py-7 sm:px-5 sm:py-9 md:px-10 md:py-12 lg:px-14">
          <PromptView prompt={active} />
        </main>
      </div>
    </div>
  );
}

export default ContentPage;
